import React from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';

import { assess, overlapWording } from './assess';
import { formatDay, formatRange, formatSlots, slotOfRecord } from './dates';
import { splitOriginal } from './executeSplit';
import { CHOICE, STATUS, describeGap, gapChoiceLabels } from './planSplit';

const TITLES = {
  [STATUS.SPLIT]: 'Ce remplacement relève de plusieurs congés',
  [STATUS.RELINK]: 'Ce remplacement relève d’un autre congé',
  [STATUS.NO_NEXT]: 'Congé lié à vérifier',
  [STATUS.ORPHANS]: 'Congé lié à vérifier',
  [STATUS.UNCOVERED]: 'Congé lié à vérifier',
  [STATUS.PENDING]: 'Absence pas encore générée',
  [STATUS.HOLE]: 'Trou dans la couverture du congé lié',
  [STATUS.COVERED]: 'Chevauchement avec une autre affectation',
};

export const describePiece = (piece, slots) => {
  const label = piece.original ? 'Ce remplacement' : 'Nouveau remplacement';
  return `${label} : ${formatDay(piece.start_date)} → ${formatDay(
    piece.end_date,
  )}, congé ${piece.leave_id} · ${formatSlots(slots)}`;
};

// « Le Shack CNP Assurances, 12/10 → 29/10, lun-ven 17:30-21:00 »
export const describeOther = (other) => {
  return `${other.account_name}, ${formatRange(
    other.start_date,
    other.end_date,
  )}, ${formatSlots([slotOfRecord(other)])}`;
};

// L'id ouvre la fiche de l'affectation chevauchée dans un nouvel onglet.
const OverlapMessage = ({ overlaps, wording }) => {
  return (
    <Typography color="error" paragraph data-testid="split-overlap">
      Ce remplacement chevauche déjà{' '}
      {overlaps.map((other, index) => {
        return (
          <React.Fragment key={other.id}>
            {index > 0 && (index === overlaps.length - 1 ? ' et ' : ', ')}
            <a
              href={`#/affectation/${other.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {other.id}
            </a>{' '}
            ({describeOther(other)})
          </React.Fragment>
        );
      })}{' '}
      : {wording.refusal}. {wording.advice}
    </Typography>
  );
};

OverlapMessage.propTypes = {
  overlaps: PropTypes.array.isRequired,
  wording: PropTypes.object.isRequired,
};

export const failureText = (result) => {
  if (result.stage === 'patch') {
    return `Care a refusé de modifier ce remplacement : ${result.message}. Rien n'a été écrit.`;
  }
  if (result.compensated) {
    return `Care a refusé de créer les remplacements suivants : ${result.message}. Ce remplacement a repris ses valeurs : rien n'a changé.`;
  }
  const { original: first, others: followers } = splitOriginal(result.pieces);
  const toCreate = followers
    .map((piece) => {
      return `congé ${piece.leave_id} du ${formatDay(
        piece.start_date,
      )} au ${formatDay(piece.end_date)}`;
    })
    .join(' ; ');
  return `Ce remplacement est arrêté au ${formatDay(first.end_date)} (congé ${
    first.leave_id
  }), mais les remplacements suivants n'ont pas été créés : ${
    result.message
  }. La remise en état a aussi échoué (${
    result.compensationMessage
  }). Créez-les à la main (${toCreate}), ou remettez la fin de ce remplacement au ${formatDay(
    String(result.previousEnd).slice(0, 10),
  )}.`;
};

const SplitReplacementDialog = ({
  state,
  create,
  onCancel,
  onChoice,
  onClose,
  onSaveAnyway,
  onSplit,
}) => {
  if (!state) {
    return null;
  }
  if (state.phase === 'loading') {
    return (
      <Dialog open>
        <DialogContent>
          <CircularProgress size={20} />{' '}
          <Typography component="span">Contrôle du congé lié…</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  const {
    plan,
    context,
    choices,
    preflightData,
    preflightError,
    result,
    phase,
  } = state;
  const { input, slots } = context;
  const view = assess({ plan, context, choices, preflightData, create });
  const working = phase === 'working';
  const failed = phase === 'failed';
  const showPreview = !!view.resolved && view.resolved.length > 0 && !failed;

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      onClose={working ? undefined : onCancel}
    >
      <DialogTitle>{TITLES[plan.status]}</DialogTitle>
      <DialogContent>
        {plan.message && (
          <Typography paragraph data-testid="split-message">
            {plan.message}
          </Typography>
        )}
        {view.overlaps.length > 0 && (
          <OverlapMessage
            overlaps={view.overlaps}
            wording={overlapWording({ ...view, plan })}
          />
        )}
        {plan.gaps.map((gap) => {
          const labels = gapChoiceLabels(gap, plan);
          return (
            <div key={gap.key} data-testid={`split-${gap.key}`}>
              <Typography>{describeGap(gap, input.absentName)}</Typography>
              <RadioGroup
                row
                value={choices[gap.key] || ''}
                onChange={(event) => {
                  onChoice(gap.key, event.target.value);
                }}
              >
                <FormControlLabel
                  value={CHOICE.ATTACH}
                  control={<Radio disabled={working || failed} />}
                  label={labels[CHOICE.ATTACH]}
                />
                <FormControlLabel
                  value={CHOICE.REMOVE}
                  control={<Radio disabled={working || failed} />}
                  label={labels[CHOICE.REMOVE]}
                />
              </RadioGroup>
            </div>
          );
        })}
        {showPreview && (
          <ul data-testid="split-preview">
            {view.resolved.map((piece) => {
              return (
                <li key={`${piece.leave_id}-${piece.start_date}`}>
                  <Typography>{describePiece(piece, slots)}</Typography>
                </li>
              );
            })}
          </ul>
        )}
        {view.dateIssues.length > 0 && (
          <div data-testid="split-issues">
            {view.dateIssues.map((message) => {
              return (
                <Typography key={message} color="error">
                  {message}
                </Typography>
              );
            })}
          </div>
        )}
        {preflightError && (
          <Typography data-testid="split-preflight-error">
            {`Pré-contrôle impossible (${preflightError}) : care tranchera à l'enregistrement.`}
          </Typography>
        )}
        {failed && (
          <Typography color="error" data-testid="split-failure">
            {failureText(result)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {failed ? (
          <Button onClick={onClose} color="primary">
            Fermer
          </Button>
        ) : (
          <>
            <Button onClick={onCancel} disabled={working}>
              Annuler
            </Button>
            <Button
              onClick={onSaveAnyway}
              disabled={working || !view.canSaveAnyway}
            >
              Enregistrer sans découper
            </Button>
            <Button
              onClick={onSplit}
              disabled={working || !view.canSplit}
              color="primary"
              variant="contained"
            >
              {working ? <CircularProgress size={20} /> : 'Découper'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

SplitReplacementDialog.propTypes = {
  state: PropTypes.object,
  create: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onChoice: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveAnyway: PropTypes.func.isRequired,
  onSplit: PropTypes.func.isRequired,
};

SplitReplacementDialog.defaultProps = {
  state: null,
  create: false,
};

export default SplitReplacementDialog;
